import { toMillis } from '../lib/lessonScoring.js'

/**
 * "This changed after you finished it."
 *
 * A completion records that somebody finished a module ON A GIVEN DAY. It does
 * not record WHAT they finished, and it never has: the quiz samples `count`
 * questions out of a larger pool with a per-attempt seed, so what a trainee sat
 * is a subset of the module, not the module. Comparing their stored answers to
 * today's questions therefore cannot tell an edited module from an ordinary
 * second sitting, and no fingerprint of the sampled subset can either.
 *
 * So freshness is decided by TIME, not by content:
 *
 *     the module changed after you completed it  ->  your completion is stale
 *
 * Both timestamps already exist on live data — `publishedAt` is rewritten on
 * the custom module every time AI Studio publishes it, and `completedAt` is
 * written on the progress document — so this works retroactively, with nothing
 * to migrate and nothing new to store.
 *
 * NOTHING IS DELETED. Staleness is derived, never written, so it reverses the
 * moment an admin accepts the current completions and it cannot destroy a
 * record the way an automatic un-completion would.
 */


/**
 * When a module's content last changed, as milliseconds. 0 when unknown.
 *
 * `acceptedAt` is an admin saying "this edit did not warrant a retake" — it
 * wins over publishedAt so that fixing a typo does not forfeit everyone's
 * score. It is the only escape hatch from the blanket rule, it is one click,
 * and it moves a date rather than touching anybody's progress.
 *
 * `contentUpdatedAt` is for BUILT-IN modules, whose content lives in code and
 * so has no publish timestamp to read. It is opt-in: an author who materially
 * changes a built-in sets it, and nothing detects the change if they do not.
 * Deliberately not inferred from a deploy — a deploy touches every module, and
 * asking everyone to retake everything after a routine release is worse than
 * missing an edit.
 */
export function moduleContentChangedAt(mod) {
  if (!mod) return 0
  const accepted = toMillis(mod.completionsAcceptedAt)
  const changed = Math.max(toMillis(mod.publishedAt), toMillis(mod.contentUpdatedAt))
  return accepted >= changed ? 0 : changed
}

/**
 * Has this module changed since this person completed it?
 *
 * False whenever we cannot honestly say yes: not completed, no completion time,
 * or a module with no change timestamp at all (every built-in that has not
 * opted in). An unknown is reported as fresh rather than guessed at, because
 * the cost of a wrong "stale" is a trainee redoing work for no reason.
 */
export function isCompletionStale(mod, progress) {
  if (!progress?.completed) return false
  const completedAt = toMillis(progress.completedAt)
  if (!completedAt) return false
  const changedAt = moduleContentChangedAt(mod)
  if (!changedAt) return false
  return changedAt > completedAt
}

/** The completed-but-stale modules in a progress map, as ids. */
export function staleModuleIds(progressMap = {}, moduleLookup = () => null) {
  return Object.keys(progressMap || {}).filter((id) =>
    isCompletionStale(moduleLookup(id), progressMap[id]),
  )
}
